/**
 * SettingsPage - Pagina Impostazioni Utente
 * 
 * Layout a due colonne con sidebar di navigazione.
 * Ogni sezione è un componente separato in /components/settings/
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { usePrimaryColor, PRIMARY_COLORS } from '../contexts/PrimaryColorContext';
import { useExtensions } from '../hooks/useExtensions';
import axios from 'axios';
import { 
  Settings, 
  Loader2, 
  Shield, 
  ChevronRight,
  User,
  Building2,
  Palette,
  MapPin,
  Package,
  Bell,
  FileText,
  CreditCard,
  Puzzle,
  Lock,
  Eye,
  Menu,
  Globe
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

// Import componenti sezioni
import {
  ProfileSection,
  PasswordSection,
  PreferencesSection,
  DeliverySitesSection,
  ExtensionsSection,
  CompanySection,
  StockSection,
  NotificationsSection,
  BillingSection,
  PublicProfileSection
} from '../components/settings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurazione tab impostazioni
interface SettingsTab {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
  roles: ('seller' | 'buyer' | 'admin' | 'operator')[];
  requiresExtension?: string;
  group: 'account' | 'operations' | 'billing' | 'security';
}

const SETTINGS_TABS: SettingsTab[] = [
  // Account & Azienda
  {
    id: 'profile',
    label: 'Profilo',
    icon: User,
    description: 'Informazioni personali e avatar',
    roles: ['seller', 'buyer', 'admin', 'operator'],
    group: 'account'
  },
  {
    id: 'company',
    label: 'Azienda',
    icon: Building2,
    description: 'Dati aziendali, logo e fatturazione',
    roles: ['seller', 'buyer'],
    group: 'account'
  },
  {
    id: 'preferences',
    label: 'Preferenze',
    icon: Palette,
    description: 'Tema, lingua e personalizzazione',
    roles: ['seller', 'buyer', 'admin', 'operator'],
    group: 'account'
  },
  // Operatività
  {
    id: 'delivery_sites',
    label: 'Siti di Consegna',
    icon: MapPin,
    description: 'Gestisci indirizzi di consegna',
    roles: ['buyer'],
    group: 'operations'
  },
  {
    id: 'stock',
    label: 'Gestione Stock',
    icon: Package,
    description: 'Configurazione inventario',
    roles: ['seller'],
    requiresExtension: 'stock',
    group: 'operations'
  },
  {
    id: 'notifications',
    label: 'Notifiche',
    icon: Bell,
    description: 'Preferenze notifiche email',
    roles: ['seller'],
    group: 'operations'
  },
  // Amministrazione
  {
    id: 'billing',
    label: 'Fatturazione',
    icon: CreditCard,
    description: 'Impostazioni pagamenti',
    roles: ['seller'],
    group: 'billing'
  },
  {
    id: 'extensions',
    label: 'Estensioni',
    icon: Puzzle,
    description: 'Moduli e abbonamenti attivi',
    roles: ['seller', 'buyer'],
    group: 'billing'
  },
  // Sicurezza
  {
    id: 'public_profile',
    label: 'Profilo Pubblico',
    icon: Globe,
    description: 'Pagina pubblica con URL personalizzata',
    roles: ['seller'],
    group: 'operations'
  },
  {
    id: 'password',
    label: 'Password',
    icon: Lock,
    description: 'Modifica password di accesso',
    roles: ['seller', 'buyer', 'admin', 'operator'],
    group: 'security'
  },
  {
    id: 'privacy',
    label: 'Privacy',
    icon: Eye,
    description: 'GDPR e gestione dati',
    roles: ['seller', 'buyer', 'admin', 'operator'],
    group: 'security'
  },
];

const GROUP_LABELS: Record<string, { label: string; icon: React.ElementType }> = {
  account: { label: 'Account', icon: User },
  operations: { label: 'Operatività', icon: Package },
  billing: { label: 'Amministrazione', icon: CreditCard },
  security: { label: 'Sicurezza', icon: Shield },
};

const SettingsPage = () => {
  const { user, isAssociatedOnly } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, locale, changeLocale } = useI18n();
  const { primaryColor, setPrimaryColor } = usePrimaryColor();
  const { 
    subscriptions, 
    features, 
    config, 
    isExtensionActive,
    isExtensionTrial,
    getTrialDaysRemaining,
    isFeatureActive,
    updateFeatures,
    startTrial,
    loading: extensionsLoading
  } = useExtensions();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Tab attiva (da URL o default)
  const activeTab = searchParams.get('tab') || 'profile';
  
  const setActiveTab = (tabId: string) => {
    setSearchParams({ tab: tabId });
    setMobileMenuOpen(false);
  };

  // Fetch dati azienda
  const fetchCompany = async () => {
    if (!user?.company_id) return;
    try {
      const res = await axios.get(`${API}/companies/my`);
      setCompany(res.data);
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchCompany();
      setLoading(false);
    };
    if (user) {
      init();
    }
  }, [user]);

  // Filtra tab in base al ruolo, estensioni e tipo di membro associazione
  const getVisibleTabs = () => {
    return SETTINGS_TABS.filter(tab => {
      // Check ruolo
      if (!tab.roles.includes(user?.role as any)) return false;
      // Check estensione richiesta
      if (tab.requiresExtension && !isExtensionActive(tab.requiresExtension)) return false;
      
      // Gli utenti "Solo Associato" non devono vedere alcune tab
      if (isAssociatedOnly) {
        // Non mostrare: preferenze (look & feel), delivery_sites, extensions, billing
        if (['preferences', 'delivery_sites', 'extensions', 'billing', 'company'].includes(tab.id)) {
          return false;
        }
      }
      
      return true;
    });
  };

  const visibleTabs = getVisibleTabs();
  const currentTab = SETTINGS_TABS.find(t => t.id === activeTab);
  
  // Raggruppa tab per categoria
  const groupedTabs = visibleTabs.reduce((acc, tab) => {
    if (!acc[tab.group]) acc[tab.group] = [];
    acc[tab.group].push(tab);
    return acc;
  }, {} as Record<string, SettingsTab[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Render contenuto sezione attiva
  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSection user={user} t={t} />;
      case 'company':
        return <CompanySection user={user} company={company} fetchCompany={fetchCompany} t={t} />;
      case 'preferences':
        return (
          <PreferencesSection 
            t={t}
            theme={theme}
            setTheme={setTheme}
            locale={locale}
            changeLocale={changeLocale}
            primaryColor={primaryColor}
            setPrimaryColor={setPrimaryColor}
            PRIMARY_COLORS={PRIMARY_COLORS}
          />
        );
      case 'delivery_sites':
        return (
          <DeliverySitesSection 
            user={user}
            t={t}
            startTrial={startTrial}
            isExtensionActive={isExtensionActive}
          />
        );
      case 'stock':
        return <StockSection user={user} t={t} isExtensionActive={isExtensionActive} />;
      case 'notifications':
        return <NotificationsSection user={user} t={t} />;
      case 'billing':
        return <BillingSection user={user} t={t} />;
      case 'extensions':
        return (
          <ExtensionsSection 
            user={user}
            subscriptions={subscriptions}
            features={features}
            config={config}
            isExtensionActive={isExtensionActive}
            isExtensionTrial={isExtensionTrial}
            getTrialDaysRemaining={getTrialDaysRemaining}
            isFeatureActive={isFeatureActive}
            updateFeatures={updateFeatures}
            startTrial={startTrial}
            extensionsLoading={extensionsLoading}
          />
        );
      case 'password':
        return <PasswordSection t={t} />;
      case 'public_profile':
        return <PublicProfileSection />;
      case 'privacy':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Privacy e GDPR
              </CardTitle>
              <CardDescription>
                Gestione dati personali e conformità normativa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Puoi richiedere l'esportazione o la cancellazione dei tuoi dati personali
                  in conformità con il GDPR. Per procedere, contatta il supporto.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" asChild>
                    <Link to="/privacy-policy">
                      <Eye className="h-4 w-4 mr-2" />
                      Privacy Policy
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/terms">
                      <FileText className="h-4 w-4 mr-2" />
                      Termini di Servizio
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return <ProfileSection user={user} t={t} />;
    }
  };

  // Componente Sidebar Navigation
  const SidebarNav = ({ onItemClick }: { onItemClick?: () => void }) => (
    <nav className="space-y-6">
      {Object.entries(groupedTabs).map(([groupId, tabs]) => {
        const group = GROUP_LABELS[groupId];
        if (!tabs.length) return null;
        
        return (
          <div key={groupId}>
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <group.icon className="h-3.5 w-3.5" />
              {group.label}
            </h3>
            <div className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    onItemClick?.();
                  }}
                  data-testid={`settings-nav-${tab.id}`}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all",
                    activeTab === tab.id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                >
                  <tab.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                  {activeTab === tab.id && (
                    <ChevronRight className="h-4 w-4 ml-auto flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen" data-testid="settings-page">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="px-4 py-4 border-b">
                  <SheetTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Impostazioni
                  </SheetTitle>
                </SheetHeader>
                <div className="p-4 overflow-y-auto max-h-[calc(100vh-80px)]">
                  <SidebarNav onItemClick={() => setMobileMenuOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <div>
              <h1 className="font-semibold">{currentTab?.label || 'Impostazioni'}</h1>
              <p className="text-xs text-muted-foreground">{currentTab?.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 flex-shrink-0 border-r bg-muted/30 min-h-[calc(100vh-64px)] sticky top-16">
          <div className="p-6">
            <div className="mb-6">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Impostazioni
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Gestisci il tuo account
              </p>
            </div>
            <SidebarNav />
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          <div className="p-6 lg:p-10 max-w-4xl">
            {/* Section Header - Desktop only */}
            <div className="hidden lg:block mb-8">
              <div className="flex items-center gap-3">
                {currentTab && <currentTab.icon className="h-6 w-6 text-primary" />}
                <div>
                  <h2 className="text-2xl font-bold">{currentTab?.label}</h2>
                  <p className="text-muted-foreground">{currentTab?.description}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="animate-in fade-in-50 duration-300">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
